
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ArticleRecommendation } from '@/types/articleTypes';
import { createOpenAILogError } from '@/utils/loggerTypes';
import { logger } from '@/utils/logger';
import { fetchArticleContent } from '@/utils/content/articleContentFetcher';
import { getRecommendationsWithChatCompletions } from '@/services/recommendationServices/chatCompletionsService';
import { ArticleWithEmbedding, UseArticleRecommendationsProps, RecommendationsResult } from '@/types/recommendationTypes';
import { API_KEYS } from '@/utils/apiKeys';

/**
 * Hook for getting article recommendations
 */
export function useArticleRecommendations(
  { featureId = 'recommended', crawlResult }: UseArticleRecommendationsProps = {}
): RecommendationsResult {
  const [recommendations, setRecommendations] = useState<ArticleRecommendation['recommendations'] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [articlesWithEmbeddings, setArticlesWithEmbeddings] = useState<ArticleWithEmbedding[]>([]);
  const [progress, setProgress] = useState<string>("");
  const [currentUrl, setCurrentUrl] = useState<string>("");
  const [currentContext, setCurrentContext] = useState<string>("");
  const [suggestedArticleUrls, setSuggestedArticleUrls] = useState<Set<string>>(new Set());
  
  // Recommendations now work independently - no automatic processing of crawl results

  const fetchRecommendations = async (url: string, additionalContext?: string) => {
    setIsLoading(true);
    setError(null);
    setProgress("Starting recommendation process...");
    console.log(`Starting recommendation process for URL: ${url}`);
    
    // Store current URL and context for potential "suggest more" requests
    setCurrentUrl(url);
    setCurrentContext(additionalContext || "");
    
    // Clear previous suggestions when starting a new request
    setSuggestedArticleUrls(new Set([url])); // Add the current URL to avoid recommending itself
    
    try {
      // Use the hardcoded API key directly from API_KEYS
      const apiKey = API_KEYS.openai.recommended;
      
      if (!apiKey) {
        throw new Error(`No API key available for recommended articles feature`);
      }
      
      // Use hardcoded model
      const model = 'gpt-4o';
      console.log(`Using model ${model} for recommendations`);
      setProgress(`Using model ${model} for recommendations`);
      
      // Get articles from crawl result for this specific request
      let availableArticles: ArticleWithEmbedding[] = [];
      
      if (crawlResult && crawlResult.pages && crawlResult.pages.length > 0) {
        availableArticles = crawlResult.pages
          .filter(page => page.url !== url)
          .map(page => {
            const titleWords = page.metadata.title?.toLowerCase().split(/\s+/).filter(w => w.length > 3) || [];
            const descriptionWords = page.metadata.description?.toLowerCase().split(/\s+/).filter(w => w.length > 3) || [];
            const initialKeywords = [...new Set([...titleWords, ...descriptionWords])].slice(0, 10);
            
            return {
              url: page.url,
              title: page.metadata.title || page.url.split('/').pop() || 'Untitled Page',
              embedding: [],
              content: page.content || page.metadata.description || page.metadata.title || 'No content available',
              keywords: initialKeywords,
              lastUpdated: Date.now()
            };
          });
      }
      
      if (availableArticles.length === 0) {
        toast.warning('No articles available for recommendations. Please crawl a website first.');
        setRecommendations([]);
        return;
      }
      
      // Fetch content for the source article
      setProgress("Fetching article content...");
      const articleContent = await fetchArticleContent(url);
      const articleTitle = availableArticles.find(a => a.url === url)?.title || 
                         url.split('/').pop()?.replace(/-/g, ' ') || 'Untitled Page';
      
      if (!articleContent) {
        throw new Error('Could not extract content from URL');
      }
      
      console.log('Article content fetched, length:', articleContent.length);
      
      // Direct call to Chat Completions API
      setProgress("Sending request to OpenAI...");
      console.log('Calling Chat Completions API directly with:');
      console.log('- API Key length:', apiKey.length);
      console.log('- Model:', model);
      console.log('- URL:', url);
      console.log('- Content length:', articleContent.length);
      console.log('- Available articles:', availableArticles.length);
      console.log('- Additional context:', additionalContext || 'None provided');
      
      const chatCompletionsResults = await getRecommendationsWithChatCompletions(
        apiKey,
        model,
        url,
        articleContent,
        articleTitle,
        availableArticles,
        additionalContext
      );
      
      console.log('Chat Completions API returned results:', chatCompletionsResults.length);
      console.log('Results sample:', chatCompletionsResults.slice(0, 2));
      
      setRecommendations(chatCompletionsResults);
      
      // Add recommended URLs to the set of suggested articles
      const newSuggestedUrls = new Set(suggestedArticleUrls);
      chatCompletionsResults.forEach(rec => newSuggestedUrls.add(rec.articleUrl));
      setSuggestedArticleUrls(newSuggestedUrls);
      
      logger.info('Successfully generated article recommendations', {
        component: 'RecommendedArticlesWidget',
        context: {
          url,
          model,
          approach: 'chat-completions'
        }
      });
      
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      
      logger.error('Failed to generate article recommendations', 
        createOpenAILogError(error.message, {
          operation: 'chat',
          model: 'gpt-4o',
          component: 'RecommendedArticlesWidget',
          url
        })
      );
      
      setError(error);
      toast.error(`Error: ${error.message}`);
      console.error('Article recommendations error details:', error);
    } finally {
      setIsLoading(false);
      setProgress("");
    }
  };

  // Function to suggest more recommendations
  const suggestMore = async () => {
    if (!currentUrl) {
      toast.warning('Please run a recommendation search first');
      return;
    }
    
    if (isLoading) {
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setProgress("Getting more recommendations...");
    
    try {
      // Get API key directly from the API_KEYS object
      const apiKey = API_KEYS.openai.recommended;
      
      if (!apiKey) {
        throw new Error(`No API key available for recommended articles feature`);
      }
      
      // Use hardcoded model
      const model = 'gpt-4o';
      
      // Get current article content
      const articleContent = await fetchArticleContent(currentUrl);
      
      // Get available articles from current crawl result
      let availableArticles: ArticleWithEmbedding[] = [];
      
      if (crawlResult && crawlResult.pages && crawlResult.pages.length > 0) {
        availableArticles = crawlResult.pages.map(page => {
          const titleWords = page.metadata.title?.toLowerCase().split(/\s+/).filter(w => w.length > 3) || [];
          const descriptionWords = page.metadata.description?.toLowerCase().split(/\s+/).filter(w => w.length > 3) || [];
          const initialKeywords = [...new Set([...titleWords, ...descriptionWords])].slice(0, 10);
          
          return {
            url: page.url,
            title: page.metadata.title || page.url.split('/').pop() || 'Untitled Page',
            embedding: [],
            content: page.content || page.metadata.description || page.metadata.title || 'No content available',
            keywords: initialKeywords,
            lastUpdated: Date.now()
          };
        });
      }
      
      const articleTitle = availableArticles.find(a => a.url === currentUrl)?.title || 
                         currentUrl.split('/').pop()?.replace(/-/g, ' ') || 'Untitled Page';
      
      // Filter out already suggested articles
      const previouslyRecommended = Array.from(suggestedArticleUrls);
      availableArticles = availableArticles.filter(a => !previouslyRecommended.includes(a.url));
      
      if (availableArticles.length === 0) {
        toast.warning('No more articles available for recommendations');
        return;
      }
      
      console.log(`Requesting 5 more recommendations, excluding ${previouslyRecommended.length} already suggested`);
      
      const moreRecommendations = await getRecommendationsWithChatCompletions(
        apiKey,
        model,
        currentUrl,
        articleContent,
        articleTitle,
        availableArticles,
        currentContext,
        true // This flag indicates we're requesting more recommendations
      );
      
      if (moreRecommendations.length === 0) {
        toast.info('No additional recommendations found');
        return;
      }
      
      // Add new recommendations to the set of suggested articles
      const newSuggestedUrls = new Set(suggestedArticleUrls);
      moreRecommendations.forEach(rec => newSuggestedUrls.add(rec.articleUrl));
      setSuggestedArticleUrls(newSuggestedUrls);
      
      // Merge with existing recommendations
      setRecommendations(prevRecs => {
        if (prevRecs) {
          return [...prevRecs, ...moreRecommendations];
        }
        return moreRecommendations;
      });
      
      toast.success(`Added ${moreRecommendations.length} more recommendations`);
      
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error occurred');
      setError(error);
      toast.error(`Error getting more recommendations: ${error.message}`);
    } finally {
      setIsLoading(false);
      setProgress("");
    }
  };

  return {
    recommendations,
    isLoading,
    error,
    fetchRecommendations,
    progress,
    suggestMore
  };
}
